import classNames from 'classnames';
import { useCallback, useState } from 'react';
import toast from 'react-hot-toast';
import {
  fileToBase64,
  getBase64BytesSize,
  getFileNameFromBase64String,
} from '@/modules/common/utils/file.ts';
import { Dropzone, TooltipNew } from 'ui';
import { TrashIcon } from 'ui/icons';
import { formatBytesSize } from 'ui/utils';

interface UploadDocumentsBlockProps {
  label?: string;
  error?: string;
  value: string[];
  classes?: {
    label?: string;
  };
  onChange: (value: string[]) => void;
}

const MAX_DOCUMENTS = 5;

const UploadDocumentsBlock = ({
  error,
  value,
  label,
  classes,
  onChange,
}: UploadDocumentsBlockProps) => {
  const [isFileLoading, setIsFileLoading] = useState(false);
  const disabled = value.length >= MAX_DOCUMENTS;

  const handleAccept = useCallback(
    async (files: File[]) => {
      setIsFileLoading(true);

      const base64FilesPromises = files.map(async (file: File) => {
        try {
          return await fileToBase64(file, true);
        } catch (e: any) {
          toast.error(`Error: ${e.message}`);
          return '';
        }
      });

      const base64Files = (await Promise.allSettled(base64FilesPromises)).reduce(
        (arr, p) => (p.status === 'fulfilled' ? [...arr, p.value] : arr),
        [] as string[],
      );

      onChange([...value, ...base64Files]);

      setIsFileLoading(false);
    },
    [onChange, value],
  );

  const handleClear = useCallback(
    (index: number) => {
      const res = [...value];
      res.splice(index, 1);
      onChange(res);
    },
    [onChange, value],
  );

  return (
    <div className="w-full flex flex-col space-y-4">
      <p className={classNames('text-[14px] leading-[22px]', classes?.label)}>
        {label || 'Upload document JPG, PNG or PDF. Up to 10 mb'}
      </p>

      <TooltipNew
        disabled={!disabled}
        text={`The maximum number of documents that can be attached is ${MAX_DOCUMENTS}.`}
      >
        <div className="[&>div>div]:min-h-[64px]">
          <Dropzone
            multiple
            acceptFormat={['jpeg', 'pdf', 'png', 'tiff', 'xls', 'doc']}
            maxFiles={MAX_DOCUMENTS - value.length}
            onFileAccepted={handleAccept}
            loading={isFileLoading}
            onFileClear={() => 0}
            error={error}
            disabled={disabled}
          />
        </div>
      </TooltipNew>
      <div className="w-full space-y-1.5">
        {value.map((item, index) => {
          const fileName = getFileNameFromBase64String(item) || '';
          const fileExtension = fileName.split('.').at(-1);

          return (
            <div
              className="flex justify-between border border-[rgba(23,_23,_23,_0.2)] rounded-lg py-2 px-4"
              key={`${index}_${item.slice(-50)}`}
            >
              <div className="w-[calc(100%_-_24px)] flex items-center gap-4">
                <div>
                  {item?.includes('pdf') ? (
                    <iframe
                      src={item}
                      className="max-h-[56px] max-w-[56px] aspect-auto overflow-hidden"
                      allowFullScreen={true}
                    ></iframe>
                  ) : (
                    <img
                      src={item}
                      alt="Loading..."
                      className={classNames('max-h-[56px] max-w-[56px] aspect-auto')}
                    />
                  )}
                </div>
                <div>
                  <div className="max-w-[100%] text-ellipsis overflow-hidden text-nowrap text-sm font-medium">
                    {getFileNameFromBase64String(item) || ''}
                  </div>
                  <div className="text-xs text-gray-500 uppercase mt-0.5">
                    <span className="pr-1">{fileExtension}</span>
                    <span className="border-r inline-block h-3 mb-[-2px]"></span>
                    <span className="pl-1">{formatBytesSize(getBase64BytesSize(item))}</span>
                  </div>
                </div>
              </div>
              <img
                src={TrashIcon}
                alt="Loading"
                width={24}
                className={classNames({ 'cursor-pointer': !isFileLoading })}
                onClick={handleClear.bind(null, index)}
              />
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default UploadDocumentsBlock;
